import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import "../../styles/App.css";
import {
  findStudentHeaderRow,
  buildStudentUploadRows,
  STUDENT_COLUMN_KEYS,
  STUDENT_COLUMN_LABELS,
} from "../../decision/studentColumns.mjs";
import { sha256Hex } from "../../decision/fileHash.js";
import ImportToSystemPanel from "./ImportToSystemPanel.js";

// Окремий імпорт студентів: список (зазвичай усього факультету) -> система.
// Без балів: ПІБ, група, курс, стать, віддаленість q, пільга p і (за наявності)
// кімната. Гуртожиток обирається в панелі завантаження; хто не живе в ньому,
// імпортується без кімнати або пропускається (за вибором).
const StudentsImportPage = () => {
  const [fileName, setFileName] = useState("");
  const [fileHash, setFileHash] = useState(null);
  const [workbook, setWorkbook] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [sheetRows, setSheetRows] = useState(null);
  const [header, setHeader] = useState(null); // { rowIndex, indices, headerEntries }
  const [manual, setManual] = useState({});
  const [parseError, setParseError] = useState("");
  const [anonymize, setAnonymize] = useState(false);

  const indices = useMemo(() => (header ? { ...header.indices, ...manual } : null), [header, manual]);

  const uploadRows = useMemo(() => {
    if (!sheetRows || !header || indices?.pib === null || indices?.pib === undefined) return [];
    return buildStudentUploadRows(sheetRows.slice(header.rowIndex + 1), indices);
  }, [sheetRows, header, indices]);

  const applySheet = (wb, name) => {
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: "" });
    const found = findStudentHeaderRow(rows);
    setSheetRows(rows);
    setManual({});
    if (!found) {
      setHeader(null);
      setParseError("Не вдалося знайти рядок заголовків із колонкою ПІБ на цьому аркуші.");
      return;
    }
    setHeader(found);
    setParseError("");
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setFileHash(null);
    setParseError("");
    setHeader(null);
    setSheetRows(null);
    try {
      const buffer = await file.arrayBuffer();
      try {
        setFileHash(await sha256Hex(buffer));
      } catch (hashErr) {
        console.error(hashErr);
      }
      const wb = XLSX.read(buffer, { type: "array" });
      setWorkbook(wb);
      setSheetName(wb.SheetNames[0]);
      applySheet(wb, wb.SheetNames[0]);
    } catch (err) {
      console.error(err);
      setParseError("Не вдалося розібрати файл. Перевірте, що це коректний .xlsx файл.");
    }
  };

  const handleSheetChange = (name) => {
    setSheetName(name);
    if (workbook) applySheet(workbook, name);
  };

  return (
    <div className="page-container">
      <h3 className="text-header">Імпорт студентів</h3>
      <p className="text-header">
        Список студентів: ПІБ, група, курс, стать, віддаленість і пільга; кімната — за
        наявності. Бали сюди не входять. Гуртожиток обирається на кроці завантаження. Кожен студент у списку
        має отримати кімнату в системі: для рядків без неї оберіть кімнату в попередньому перегляді або приберіть їх
        з імпорту (можна одразу всі).
      </p>

      <div className="input-component">
        <p className="text-header">Файл списку студентів (.xlsx):</p>
        <input
          type="file"
          accept=".xlsx"
          aria-label="Файл списку студентів (.xlsx)"
          className="input-component"
          onChange={handleFileChange}
        />
        {fileName && <p className="text-header">Обрано файл: {fileName}</p>}
        <label className="text-header">
          <input type="checkbox" checked={anonymize} onChange={(e) => setAnonymize(e.target.checked)} /> Знеособити
          (ПІБ, без кімнат)
        </label>
        {parseError && (
          <p className="text-header" role="alert" style={{ color: "var(--full-busy)" }}>
            {parseError}
          </p>
        )}
      </div>

      {workbook && workbook.SheetNames.length > 1 && (
        <div className="header-content">
          <div className="input-block text-header">
            <p className="text-header">Аркуш:</p>
            <select
              className="header-content-select"
              aria-label="Аркуш"
              value={sheetName}
              onChange={(e) => handleSheetChange(e.target.value)}
            >
              {workbook.SheetNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {header && (
        <div className="input-component" data-testid="student-columns">
          <p className="text-header">Колонки файлу (перевірте зіставлення):</p>
          <div className="form-container column-map">
            {STUDENT_COLUMN_KEYS.map((key) => (
              <div className="input-block text-header" key={key}>
                <p className="text-header">
                  {STUDENT_COLUMN_LABELS[key]}
                  {key === "pib" ? " *" : ""}
                </p>
                <select
                  className="header-content-select"
                  aria-label={`Колонка: ${STUDENT_COLUMN_LABELS[key]}`}
                  value={indices[key] === null || indices[key] === undefined ? "" : indices[key]}
                  onChange={(e) =>
                    setManual((prev) => ({ ...prev, [key]: e.target.value === "" ? null : Number(e.target.value) }))
                  }
                >
                  <option value="">— немає —</option>
                  {header.headerEntries.map((entry) => (
                    <option key={entry.index} value={entry.index}>
                      {entry.text}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <p className="text-header">Розпізнано рядків: {uploadRows.length}.</p>
          {indices.zone === null && (
            <p className="text-header">
              Колонки віддаленості немає: її можна задати для всіх рядків у попередньому перегляді (кнопка «Задати
              віддаленість за замовчуванням»).
            </p>
          )}
        </div>
      )}

      {uploadRows.length > 0 && (
        <div className="input-component">
          <ImportToSystemPanel
            mode="students"
            rows={uploadRows}
            fileHash={fileHash}
            sheetName={sheetName}
            anonymize={anonymize}
            disabledReason={fileHash ? "" : "Не вдалося порахувати хеш файлу (потрібне безпечне з'єднання HTTPS)."}
          />
        </div>
      )}
    </div>
  );
};

export default StudentsImportPage;
